import { Fragment, useRef, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faMicrophone } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames/bind";
import styles from "~/pages/btai-chatbots/AIChatbot/AIChatbot.module.scss";
import {TypeWriterText, TypingIndicator,} from "~/components";
import {RecognizeTextAsync} from '~/aws/lexClient';

const cx = classNames.bind(styles);

function AwsChatbotTesting() {
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [sessionId] = useState("1760689625586");
    const textareaRef = useRef(null);
    const chatEndRef = useRef(null);

    const chatQuestionsDefault = [
        "What is love?",
        "What is success?",
        "How can you gain long-life success, health, and happiness?",
        "How can I stay healthy?",
        "What is pseudo-success?",
    ];

    const handleInput = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "auto";
            textarea.style.height = Math.min(textarea.scrollHeight, 130) + "px";
        }
    };

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages]);

    const handleSendMessage = async () => {
        const trimmed = messageInput.trim();
        if (!trimmed) return;

        setMessages((prev) => [
            ...prev,
            { ChatTypeId: 1, ChatContent: trimmed },
        ]);
        setMessageInput("");
        await handleRecognizeTextAsync(trimmed);
    };

    const handleRecognizeTextAsync = async (textInput) => {
        if (textInput === "") return;
        setIsTyping(true)
        const res = await RecognizeTextAsync(textInput, sessionId);
        if(res.success){
            setIsTyping(false);
            const buttons = res.data.messages[1].imageResponseCard.buttons;
            const newButtons = buttons.map(item => ({
                Text: item.text,
                Value: item.value
            }));
            setMessages((prev) => [
                ...prev,
                {
                    ChatTypeId: 2,
                    ChatContent: res.data.messages[0].content,
                    ChatButtons: newButtons,
                    isNew: true,
                    showButtons: false,
                },
            ]);
        }
    }

    const handleKeyDown = async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            await handleSendMessage();
        }
    };

    const handleChatButton = async (buttonValue, buttonText) => {
        if (!buttonValue.trim()) return;
        setMessages((prev) => [
            ...prev,
            { ChatTypeId: 1, ChatContent: buttonText.trim() },
        ]);
        await handleRecognizeTextAsync(buttonValue.trim());
    };

    const updateMessageAtIndex = (index, updates) => {
        setMessages((prev) =>
            prev.map((msg, i) => (i === index ? { ...msg, ...updates } : msg))
        );
    };

    const renderMessage = (message, index) => {
        if (message.ChatTypeId === 1) {
            return (
                <div key={index} className="d-flex chat-message justify-content-end pb-4">
                    <div className={cx("right-message", "chat-message-content", "fs-8-1", "border")}>
                        {message.ChatContent}
                    </div>
                </div>
            );
        }

        return (
            <div key={index} className="d-flex chat-message pb-4 flex-column">
                <div className={cx("chat-message-content", "fs-8-1")}>
                    {message.isNew ? (
                        <TypeWriterText
                            text={message.ChatContent}
                            onComplete={() => updateMessageAtIndex(index, {
                                isNew: false,
                                showButtons: true
                            })}
                        />
                    ) : (
                        message.ChatContent
                    )}
                </div>

                {message.showButtons && message.ChatButtons && (
                    <div className={cx("chat-buttons", "d-flex mt-2 mb-2")}>
                        {message.ChatButtons.map((button, idx) => (
                            <div
                                key={idx}
                                title={button.Text}
                                className={cx("chat-button", "fs-8-1 border", "cursor-pointer")}
                                onClick={() => handleChatButton(button.Value, button.Text)}
                            >
                            <span className={cx("button-text", "fs-9", "fw-bold")}>
                              {button.Text}
                            </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={cx("chat-container")} style={{ height: "78vh" }}>
            <div className={cx("chat-container__body")}>
                <div className={cx("chat-content")} ref={chatEndRef}>
                    {messages.length > 0 ? (
                        <Fragment>
                            {messages.map((message, index) => renderMessage(message, index))}
                            {isTyping && (
                                <div className="d-flex chat-message pb-4 flex-column">
                                    <div className={cx("chat-message-content", "fs-8-1")}>
                                        <TypingIndicator />
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        <div style={{ marginTop: "25vh" }}>
                            <h4 className="mb-3 text-center">What can I help with?</h4>
                            <div className="d-flex justify-content-center">
                                <div
                                    className={cx(
                                        "chat-buttons",
                                        "d-flex mt-2 mb-2 justify-content-center"
                                    )}
                                >
                                    {chatQuestionsDefault.map((question, index) => (
                                        <div
                                            key={index}
                                            title={question}
                                            className={cx(
                                                "chat-button",
                                                "fs-8-1 border",
                                                "cursor-pointer"
                                            )}
                                            onClick={() => handleChatButton(question, question)}
                                        >
                                        <span
                                          className={cx("button-text", "fs-9", "fw-bold")}
                                        >
                                        {question}
                                        </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* FOOTER */}
            <div className={cx("chat-footer", "w-100")}>
                <div className={cx("chat-textarea-container", "bg-body-emphasis")}>
                    <textarea
                      ref={textareaRef}
                      onInput={handleInput}
                      placeholder="Type message here..."
                      className={cx("chat-textarea", "fs-8-1", "ps-2")}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <div className="d-flex justify-content-end">
                        <div className={cx("icon-action")}>
                            <FontAwesomeIcon className="fs-9" icon={faMicrophone} />
                        </div>
                        <div
                            className={cx("icon-action", "me-3")}
                            onClick={handleSendMessage}
                        >
                            <FontAwesomeIcon className="fs-9" icon={faPaperPlane} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AwsChatbotTesting;
